import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Utils } from "ng/src/app/core/utils";
import { ProvisioningService } from "ng/src/app/log-settings/provisioning/provisioning.service";
import { createSentinelDoor, createSentinelFacility, RequiredSentinelFacility, SentinelDoor, SentinelEntry, SentinelFacility } from "ng/src/app/log-settings/provisioning/sentinel-provision/sentinel-provision.model";
import { DataType, LogConfig } from "ng/src/app/logs/log.model";
import { LogService } from "ng/src/app/logs/log.service";
import { ObservableValueMap } from "ng/src/app/shared/model/app.model";
import { BehaviorSubject, combineLatest, forkJoin, of, Subject } from "rxjs";
import { map, skip, startWith, switchMap, take, takeUntil } from "rxjs/operators";

@Injectable()
export class SentinelProvisioningService implements OnDestroy {
	
	readonly entries = new BehaviorSubject<SentinelEntry[]>([]);
	
	get selectedFacility() {
		return this._selectedFacility;
	}
	
	set selectedFacility(facility) {
		if (facility && this.entries.value.indexOf(facility) === -1) {
			return;
		}
		
		this._selectedFacility = facility;
	}
	
	_selectedFacility?: SentinelEntry;
	
	readonly editingFacility = new BehaviorSubject<SentinelFacility | undefined>(undefined);
	readonly editingDoor = new BehaviorSubject<SentinelDoor | undefined>(undefined);
	
	readonly refresh$ = new Subject<void>();
	
	private _onDestroy = new Subject<void>();
	
	constructor(
		public provisioningService: ProvisioningService,
		private logService: LogService,
		private http: HttpClient
	) {
		const trigger = combineLatest([
			this.provisioningService.selectedLog,
			this.refresh$.pipe(
				startWith({})
			)
		])
			.pipe(
				map(([selectedLog]) => selectedLog)
			);
		
		trigger
			.pipe(
				takeUntil(this._onDestroy),
				switchMap(log => {
					const toReturn = {
						doors: of(new Array<LogConfig>()),
						facilities: of(new Array<LogConfig>())
					}
					if (log != null) {
						const doorOb = this.logService.getLogConfigValues({key: 'Sentinel.instance.Door%', type: 'keyLike'}, log.id, 'logbook.provision');
						const facilityOb = this.logService.getLogConfigValues({key: 'Sentinel.instance.Facility%', type: 'keyLike'}, log.id, 'logbook.provision');
						
						toReturn.doors = doorOb;
						toReturn.facilities = facilityOb;
					}
					
					return forkJoin(toReturn)
						.pipe(
							map(({doors, facilities}: ObservableValueMap<typeof toReturn>) => {
								const sDoors = doors.map(c => {
									const toReturn = JSON.parse(c.value) as SentinelDoor;
									toReturn.id = c.id;
									return toReturn;
								});
								const sFacilities = facilities.map(c => {
									const toReturn = JSON.parse(c.value) as SentinelFacility;
									toReturn.id = c.id;
									return toReturn;
								});
								
								return {doors: sDoors, facilities: sFacilities};
							})
						)
				})
			)
			.subscribe(({doors, facilities}) => {
				const next = facilities.map(f => {
					return {
						label: f.facilityName ?? 'Unknown',
						facility: f,
						doors: doors.filter(d => {
							if (!d.facility) {
								return false;
							}
							
							try {
								const url = new URL(d.facility);
								const facilityIdStr = url.pathname.split('/').pop();
								return parseInt(facilityIdStr!) === f.id;
							} catch (e) {
								console.error(e);
								return false;
							}
						}).sort(Utils.createSortFunction('accessPointName'))
					}
				}).sort(Utils.createSortFunction((e) => e.facility.facilityName));
				
				this.entries.next(next);
				const matchedForSelected = next.find(e => e.facility.id === this.selectedFacility?.facility.id);
				if (matchedForSelected) {
					this.selectedFacility = matchedForSelected;
				}
				
			});
	}
	
	editNewFacility() {
		const newFacility = createSentinelFacility();
		this.editingFacility.next(newFacility);
	}
	
	editFacility(facility: SentinelFacility) {
		this.editingFacility.next(facility);
	}
	
	editDoor(door?: SentinelDoor) {
		const editDoor = door ? door : createSentinelDoor();
		this.editingDoor.next(editDoor);
	}
	
	async deleteEntity(id: number) {
		await this.logService.deleteLogConfig(id).toPromise();
		this.refresh$.next();
	}
	
	async deleteFacility(entry: SentinelEntry) {
		await Promise.all([
			...entry.doors.map(d => this.deleteEntity(d.id!)),
			this.deleteEntity(entry.facility.id!)
		]);
		
		this.refresh$.next();
		await this.entries.pipe(
			takeUntil(this._onDestroy),
			skip(1),
			take(1)
		).toPromise();
		
		if (this.selectedFacility === entry) {
			this.selectedFacility = undefined;
		}
	}
	
	async saveFacility(facility: RequiredSentinelFacility, stopEditing = false): Promise<void> {
		const log = this.provisioningService.selectedLog.value;
		if (log == null) {
			throw new Error('No log selected');
		}
		
		facility.log = log._links.self.href;
		(facility as any)['badgeLogoImage'] = null;

		(facility as any)['requiredFields'] = Object.entries(facility.dataEntry).reduce((arr, [key, value]) => {
			if (value === 'required') {
				arr.push(key as keyof RequiredSentinelFacility['dataEntry']);
			}
			return arr;
		}, [] as (keyof RequiredSentinelFacility['dataEntry'])[]);
		
		let p: Promise<unknown>;
		if (facility.id == null || facility.id < 0) {
			p = this.logService.postLogConfig({
				hierarchy: 'logbook.provision',
				key: `Sentinel.instance.Facility.${facility.facilityName}`,
				value: JSON.stringify(facility),
				logId: log.id,
			}, DataType.LONG_TEXT)
				.toPromise();
		}
		else {
			p = this.logService.patchLogConfig(facility.id, {
				value: JSON.stringify(facility),
			})
				.toPromise();
		}
		
		await p;
		if (stopEditing) {
			this.editingFacility.next(undefined);
		}
		
		this.refresh$.next();
	}
	
	async saveDoor(facility: Pick<RequiredSentinelFacility, 'facilityName' | 'id'>, door: Required<SentinelDoor>, stopEditing = false): Promise<void> {
		const log = this.provisioningService.selectedLog.value;
		if (log == null) {
			throw new Error('No log selected');
		}
		
		if (facility.id == null || facility.facilityName == null) {
			throw new Error('Facility id and name must be provided');
		}
		
		door.badgePrinting = 'disabled';
		door.log = log._links.self.href;
		door.facility = `${window.location.origin}/api/logConfigs/${facility.id}`;
		
		let p: Promise<unknown>;
		if (door.id == null || door.id < 0) {
			p = this.logService.postLogConfig({
				hierarchy: 'logbook.provision',
				key: `Sentinel.instance.Door.${facility.facilityName} - ${door.accessPointName}`,
				value: JSON.stringify(door),
				logId: log.id,
			}, DataType.LONG_TEXT)
				.toPromise();
		}
		else {
			p = this.logService.patchLogConfig(door.id, {
				value: JSON.stringify(door),
			})
				.toPromise();
		}
		
		await p;
		this.editingDoor.next(undefined);
		this.refresh$.next();
		
		if (stopEditing) {
			this.editingDoor.next(undefined);
		}
	}
	
	provisionDoor(id: number): Promise<string> {
		return this.http.patch<{provisionCode: string}>(`/api/logConfigs/${id}`, {provision: true})
			.pipe(
				map(r => r.provisionCode)
			).toPromise();
	}
	
	ngOnDestroy(): void {
		this.editingFacility.complete();
		this.editingDoor.complete();
		this._onDestroy.next();
	}
}